import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

/**
 * Reputation Hub review widget — preserves original embed height,
 * no clipping, no scrollbars, no forced max-height.
 *
 * The third-party script + iframe (heavy: widget JS + reviewer avatars) are
 * loaded only once this section scrolls near the viewport, keeping them off
 * the initial critical path.
 */
const ReviewWidget = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  // Reveal (and therefore load) the widget when it approaches the viewport.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // Inject the vendor script only after the section is in view.
  useEffect(() => {
    if (!inView) return;
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, [inView]);

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-1 mb-3"
          >
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-secondary text-secondary" />
            ))}
          </motion.div>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Real reviews from real Johnson County, Kansas customers.
          </p>
        </div>

        <motion.div
          ref={wrapRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="review-widget-wrap max-w-5xl mx-auto"
        >
          {inView ? (
            <iframe
              className="lc_reviews_widget"
              src={siteConfig.widgets.reviewIframeSrc}
              frameBorder={0}
              scrolling="no"
              style={{ minWidth: "100%", width: "100%", border: "none", display: "block" }}
              title="Customer reviews"
            />
          ) : (
            <div
              className="w-full min-h-[420px] rounded-xl bg-muted/40 animate-pulse"
              aria-hidden="true"
            />
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewWidget;
